import { useState, useEffect } from "react";
import styles from "../Owner/Propietario.module.css";
import stepsStyles from "../steps.module.css";
import Modal from "../../../Modals/ModalAuthorized";
import { FieldError, CedulaStatus, SectionLabel, VehiculosField } from "../../../FieldHelpers";
import { useCedulaLookup } from "../../../../services/useCedulaLookup";
import { validateAutorizado } from "../../../../services/validationRules";
import { nuevaLlave } from "../../../../services/residentService";

const EMPTY = {
  id: null,
  cedula: "",
  nombre: "",
  apellidos: "",
  vehiculos: [],
  relacion: "Familiar",
};

export default function Autorizados({ setFormData, formData }) {
  const [autorizado, setAutorizado] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { buscar, resultado, estado, reset } = useCedulaLookup();

  // Auto-rellenar nombre/apellidos del TSE
  useEffect(() => {
    if (resultado) {
      setAutorizado((prev) => ({
        ...prev,
        nombre: resultado.nombre || prev.nombre,
        apellidos: resultado.apellidos || prev.apellidos,
      }));
    }
  }, [resultado]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setAutorizado((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function handleCedulaBlur() {
    buscar(autorizado.cedula);
  }

  function handleSave(e) {
    e.preventDefault();
    const errs = validateAutorizado(autorizado);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      autorizados: [...prev.autorizados, { ...autorizado, id: nuevaLlave() }],
    }));
    setAutorizado(EMPTY);
    setErrors({});
    reset();
  }

  function handleDelete(id) {
    setFormData((prev) => ({
      ...prev,
      autorizados: prev.autorizados.filter((auth) => auth.id !== id),
    }));
  }

  return (
    <div className="step">

      <h3 className="p-0 mt-2 mb-0 title" style={{fontSize:"1.3rem"}}>Información de Autorizados</h3>
      <p className={`${stepsStyles["inactive"]} mb-3` } style={{fontSize:"1rem"}}>

        Agrega personas autorizadas a la filial.{" "}
        <span style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>
          (opcional)
        </span>

      </p>

      {/* En pantallas grandes: formulario a la izquierda, lista a la derecha */}
      <div className="row g-4">
        <div className="col-12 col-xl-6">
          <div className={stepsStyles["form-wrapper"]}>
            {/* Error banner */}
            {Object.keys(errors).length > 0 && (
              <div
                style={{
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderLeft: "3px solid #ef4444",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#f87171",
                  fontSize: "0.8rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <i className="fa-solid fa-triangle-exclamation" />
                Corregí los campos antes de agregar el autorizado.
              </div>
            )}

            <SectionLabel icon="fa-user-check">Datos del autorizado</SectionLabel>

            {/* Cédula con búsqueda */}
            <div className="form-group">
              <label className="form-label">Cédula</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  name="cedula"
                  type="text"
                  className={`${stepsStyles["form-control"]} form-control`}
                  style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
                  placeholder="Ej: 123456789"
                  value={autorizado.cedula}
                  onChange={handleChange}
                  onBlur={handleCedulaBlur}
                />
                <button
                  type="button"
                  onClick={() => buscar(autorizado.cedula)}
                  disabled={estado === "buscando"}
                  title="Buscar datos en el TSE"
                  style={{
                    padding: "0 12px",
                    background: "rgba(249,115,22,0.15)",
                    border: "1px solid rgba(249,115,22,0.4)",
                    borderRadius: 6,
                    color: "#f97316",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontSize: "0.78rem",
                  }}
                >
                  {estado === "buscando" ? (
                    <i className="fa-solid fa-circle-notch fa-spin" />
                  ) : (
                    <>
                      <i className="fa-solid fa-magnifying-glass" style={{ marginRight: 4 }} />
                      Buscar
                    </>
                  )}
                </button>
              </div>
              <CedulaStatus estado={estado} />
              <FieldError msg={errors.cedula} />
            </div>

            {/* Nombre y Apellidos */}
            <div className="form-group row g-3 g-md-4">
              <div className="col-12 col-md-6">
                <label className="form-label">Nombre</label>
                <input
                  name="nombre"
                  type="text"
                  className={`${stepsStyles["form-control"]} form-control`}
                  style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
                  placeholder="Nombre"
                  value={autorizado.nombre}
                  onChange={handleChange}
                />
                <FieldError msg={errors.nombre} />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Apellidos</label>
                <input
                  name="apellidos"
                  type="text"
                  className={`${stepsStyles["form-control"]} form-control`}
                  style={{ borderColor: errors.apellidos ? "#ef4444" : undefined }}
                  placeholder="Apellidos"
                  value={autorizado.apellidos}
                  onChange={handleChange}
                />
                <FieldError msg={errors.apellidos} />
              </div>
            </div>

            {/* Relación y Vehículos */}
            <div className="form-group row g-3 g-md-4">
              <div className="col-12 col-md-5">
                <label className="form-label">Relación</label>
                <select
                  name="relacion"
                  className={`${stepsStyles["form-select"]} form-select ${stepsStyles["form-control"]} form-control`}
                  value={autorizado.relacion}
                  onChange={handleChange}
                >
                  <option value="Familiar">Familiar</option>
                  <option value="Inquilino">Inquilino</option>
                  <option value="Empleado">Empleado</option>
                </select>
              </div>
              <div className="col-12 col-md-7">
                <label className="form-label">Vehículos</label>
                <VehiculosField
                  vehiculos={autorizado.vehiculos}
                  errors={errors.vehiculos}
                  controlClass={`${stepsStyles["form-control"]} form-control`}
                  onChange={(vehiculos) => setAutorizado((prev) => ({ ...prev, vehiculos }))}
                />
              </div>
            </div>

            <button
              className={styles.btn}
              onClick={handleSave}
              style={{ alignSelf: "flex-start" }}
            >
              <i className="fa-solid fa-plus" style={{ marginRight: 6 }} />
              Agregar Autorizado
            </button>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          {/* Lista de autorizados agregados */}
          <AuthorizedList
            formData={formData}
            setFormData={setFormData}
            handleDelete={handleDelete}
          />
        </div>
      </div>
    </div>
  );
}

// ── Lista + modal de edición ─────────────────────────────────────────────────
function AuthorizedList({ formData, setFormData, handleDelete }) {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  return (
    <>
      <SectionLabel icon="fa-list">
        Autorizados agregados ({formData.autorizados.length})
      </SectionLabel>
      <div className="d-flex flex-column" style={{ gap: "6px", marginTop: 12 }}>
        {formData.autorizados.length === 0 ? (
          <div
            style={{
              padding: "18px 14px",
              border: "1px dashed var(--border-card)",
              borderRadius: 8,
              color: "var(--text-muted)",
              fontSize: "0.85rem",
              textAlign: "center",
            }}
          >
            Todavía no hay autorizados. Los que agregues aparecen aquí.
          </div>
        ) : (
          formData.autorizados.map((auth) => (
            <AuthorizedCard
              key={auth.id}
              authorized={auth}
              onDelete={handleDelete}
              openEdit={(id) => {
                setSelectedIndex(id);
                setOpen(true);
              }}
            />
          ))
        )}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        selectedIndex={selectedIndex}
      />
    </>
  );
}

function AuthorizedCard({ authorized, onDelete, openEdit }) {
  const placas = (authorized.vehiculos || []).map((v) => v.placa).filter(Boolean).join(", ");
  return (
    <div className={styles.infoAuthorized}>
      <div className={styles.iconAuthorized}>
        <i className="fa-solid fa-user-check" />
      </div>
      <div className={styles.identityAuthorized}>
        <h2 style={{ fontSize: "16px", margin: 0 }}>
          {authorized.nombre} {authorized.apellidos}
          {authorized.cedula && (
            <span
              style={{ color: "var(--text-secondary)", fontWeight: 400, fontSize: "13px" }}
            >
              {" "}
              ({authorized.cedula})
            </span>
          )}
        </h2>
        <p style={{ fontSize: "13px", margin: 0, color: "var(--text-muted)" }}>
          <span
            style={{
              display: "inline-block",
              background: "rgba(249,115,22,0.1)",
              border: "1px solid rgba(249,115,22,0.25)",
              borderRadius: 4,
              padding: "1px 6px",
              fontSize: "11px",
              color: "#fb923c",
              marginRight: 6,
            }}
          >
            {authorized.relacion}
          </span>
          {placas && `🚗 ${placas}`}
        </p>
      </div>
      <div className={styles.actionsAuthorized}>
        <button
          className={styles["icon-btn"]}
          type="button"
          onClick={() => openEdit(authorized.id)}
        >
          <i className="fa-solid fa-square-pen" />
        </button>
        <button
          className={styles["icon-btn"]}
          type="button"
          onClick={() => onDelete(authorized.id)}
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>
    </div>
  );
}
